// Zod validation schemas for the Gallery Platform
import { z } from 'zod';

// =============================================================================
// Auth Schemas
// =============================================================================

export const RegisterSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^a-zA-Z0-9]/, 'Password must contain at least one special character'),
});

export const LoginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export const PasswordResetRequestSchema = z.object({
  email: z.string().email('Invalid email address'),
});

export const PasswordResetSchema = z.object({
  token: z.string().min(1, 'Token is required'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^a-zA-Z0-9]/, 'Password must contain at least one special character'),
});

// =============================================================================
// Artist Schemas
// =============================================================================

export const CreateArtistSchema = z.object({
  displayName: z.string().min(1, 'Display name is required').max(100),
  bio: z.string().max(2000).optional(),
  solWallet: z.string().optional(),
  ethWallet: z.string().optional(),
});

export const UpdateArtistSchema = z.object({
  displayName: z.string().min(1).max(100).optional(),
  bio: z.string().max(2000).optional(),
  solWallet: z.string().optional(),
  ethWallet: z.string().optional(),
});

// =============================================================================
// Gallery Schemas
// =============================================================================

export const GalleryVisibilitySchema = z.enum(['public', 'private', 'unlisted']);

export const CreateGallerySchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  description: z.string().max(5000).optional(),
  visibility: GalleryVisibilitySchema.default('private'),
  entryPriceCents: z.number().int().min(0).default(0),
});

export const UpdateGallerySchema = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(5000).optional(),
  visibility: GalleryVisibilitySchema.optional(),
  entryPriceCents: z.number().int().min(0).optional(),
});

// =============================================================================
// Artwork Schemas
// =============================================================================

export const ArtworkTypeSchema = z.enum(['image', 'video']);

export const CreateArtworkSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  description: z.string().max(2000).optional(),
  type: ArtworkTypeSchema,
});

export const UpdateArtworkSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).optional(),
  orderIndex: z.number().int().min(0).optional(),
});

// =============================================================================
// Event Schemas
// =============================================================================

export const EventPackageSchema = z.enum(['basic', 'pro', 'immersive']);

export const CreateEventSchema = z.object({
  customerName: z.string().min(1, 'Customer name is required').max(200),
  customerEmail: z.string().email('Invalid email address'),
  eventDate: z.string().datetime('Invalid date format'),
  venue: z.string().min(1, 'Venue is required').max(500),
  package: EventPackageSchema,
  notes: z.string().max(2000).optional(),
});

export const UpdateEventSchema = z.object({
  customerName: z.string().min(1).max(200).optional(),
  customerEmail: z.string().email().optional(),
  eventDate: z.string().datetime().optional(),
  venue: z.string().min(1).max(500).optional(),
  package: EventPackageSchema.optional(),
  notes: z.string().max(2000).optional(),
  status: z.enum(['draft', 'confirmed', 'in_progress', 'completed', 'cancelled']).optional(),
});

// =============================================================================
// Playlist Schemas
// =============================================================================

export const TransitionTypeSchema = z.enum(['fade', 'cut', 'slide']);

export const CreatePlaylistSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
});

export const CreatePlaylistItemSchema = z.object({
  artworkId: z.string().uuid('Invalid artwork ID'),
  durationMs: z.number().int().min(1000).max(3600000), // 1s to 1hr
  transition: TransitionTypeSchema.default('fade'),
  zone: z.string().max(50).optional(),
});

export const UpdatePlaylistItemSchema = z.object({
  durationMs: z.number().int().min(1000).max(3600000).optional(),
  transition: TransitionTypeSchema.optional(),
  zone: z.string().max(50).optional(),
  orderIndex: z.number().int().min(0).optional(),
});

// =============================================================================
// Media Lease Schemas
// =============================================================================

export const LeaseRequestSchema = z.object({
  artworkId: z.string().uuid('Invalid artwork ID'),
  roomCode: z.string().length(6).optional(),
  screenId: z.string().uuid().optional(),
});

// =============================================================================
// WebSocket Message Schemas
// =============================================================================

export const HelloScreenSchema = z.object({
  type: z.literal('HELLO_SCREEN'),
  payload: z.object({
    screenId: z.string().uuid(),
    capabilities: z.array(z.string()).optional(),
  }),
});

export const HelloControllerSchema = z.object({
  type: z.literal('HELLO_CONTROLLER'),
  payload: z.object({
    userId: z.string().uuid(),
    authToken: z.string(),
  }),
});

export const PairClaimSchema = z.object({
  type: z.literal('PAIR_CLAIM'),
  payload: z.object({
    roomCode: z.string().length(6),
  }),
});

export const CommandTypeSchema = z.enum([
  'START',
  'PAUSE',
  'NEXT',
  'PREV',
  'SEEK',
  'SET_PLAYLIST',
  'SET_ZONE_LAYOUT',
]);

export const CommandSchema = z.object({
  type: z.literal('CMD'),
  payload: z.object({
    cmdId: z.string().uuid(),
    cmdType: CommandTypeSchema,
    data: z.unknown().optional(),
  }),
});

export const HeartbeatSchema = z.object({
  type: z.literal('HEARTBEAT'),
  payload: z.object({
    screenId: z.string().uuid(),
    ts: z.number(),
  }),
});

export const WSMessageSchema = z.discriminatedUnion('type', [
  HelloScreenSchema,
  HelloControllerSchema,
  PairClaimSchema,
  CommandSchema,
  HeartbeatSchema,
]);

// =============================================================================
// Query Schemas
// =============================================================================

export const PaginationSchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(20),
  offset: z.coerce.number().int().min(0).default(0),
});

export const GalleryQuerySchema = PaginationSchema.extend({
  visibility: GalleryVisibilitySchema.optional(),
  artistId: z.string().uuid().optional(),
  search: z.string().max(200).optional(),
});

// =============================================================================
// NFT Schemas
// =============================================================================

export const NFTChainSchema = z.enum(['solana', 'ethereum']);

export const ConnectWalletSchema = z.object({
  chain: NFTChainSchema,
  walletAddress: z.string().min(32).max(64),
  signature: z.string().min(64), // Base58 signature for Solana, hex for Ethereum
  message: z.string(), // The message that was signed
  timestamp: z.number().int(), // Unix timestamp when signature was created
});

export const MintNFTSchema = z.object({
  artworkId: z.string().uuid(),
  chain: NFTChainSchema.default('solana'),
  editionTotal: z.number().int().min(1).max(10000).default(1),
  editionNumber: z.number().int().min(1).default(1),
  recipientWallet: z.string().min(32).max(64).optional(), // If not provided, mint to connected wallet
});

export const UpdateNFTSchema = z.object({
  ownerWallet: z.string().min(32).max(64).optional(),
  txSignature: z.string().optional(),
  status: z.enum(['pending', 'minting', 'minted', 'failed']).optional(),
});

export const NFTQuerySchema = PaginationSchema.extend({
  artworkId: z.string().uuid().optional(),
  ownerWallet: z.string().optional(),
  chain: NFTChainSchema.optional(),
  status: z.enum(['pending', 'minting', 'minted', 'failed']).optional(),
});

// =============================================================================
// Type Exports (inferred from schemas)
// =============================================================================

export type RegisterInput = z.infer<typeof RegisterSchema>;
export type LoginInput = z.infer<typeof LoginSchema>;
export type CreateArtistInput = z.infer<typeof CreateArtistSchema>;
export type UpdateArtistInput = z.infer<typeof UpdateArtistSchema>;
export type CreateGalleryInput = z.infer<typeof CreateGallerySchema>;
export type UpdateGalleryInput = z.infer<typeof UpdateGallerySchema>;
export type CreateArtworkInput = z.infer<typeof CreateArtworkSchema>;
export type UpdateArtworkInput = z.infer<typeof UpdateArtworkSchema>;
export type CreateEventInput = z.infer<typeof CreateEventSchema>;
export type UpdateEventInput = z.infer<typeof UpdateEventSchema>;
export type CreatePlaylistInput = z.infer<typeof CreatePlaylistSchema>;
export type CreatePlaylistItemInput = z.infer<typeof CreatePlaylistItemSchema>;
export type UpdatePlaylistItemInput = z.infer<typeof UpdatePlaylistItemSchema>;
export type LeaseRequestInput = z.infer<typeof LeaseRequestSchema>;
export type PaginationInput = z.infer<typeof PaginationSchema>;
export type GalleryQueryInput = z.infer<typeof GalleryQuerySchema>;
export type ConnectWalletInput = z.infer<typeof ConnectWalletSchema>;
export type MintNFTInput = z.infer<typeof MintNFTSchema>;
export type UpdateNFTInput = z.infer<typeof UpdateNFTSchema>;
export type NFTQueryInput = z.infer<typeof NFTQuerySchema>;
